import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LogOut,
  Stethoscope,
  Package,
  MailOpen,
  Plus,
  Pencil,
  Trash2,
  ExternalLink,
  RefreshCw,
  Download,
  KeyRound,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  adminListProducts,
  adminCreateProduct,
  adminUpdateProduct,
  adminDeleteProduct,
  adminListContact,
  adminDeleteContact,
  adminExportContactCSV,
  formatApiError,
} from "@/lib/api";
import ProductFormDialog from "@/components/admin/ProductFormDialog";
import ChangePasswordDialog from "@/components/admin/ChangePasswordDialog";

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
};

const StatCard = ({ icon: Icon, label, value, accent = "bg-primary" }) => (
  <div className="bg-white border border-border rounded-2xl p-5 flex items-center gap-4">
    <span
      className={`w-12 h-12 rounded-2xl text-white grid place-items-center shrink-0 ${accent}`}
    >
      <Icon className="w-5 h-5" />
    </span>
    <div>
      <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
        {label}
      </p>
      <p className="font-heading font-bold text-2xl text-foreground">{value}</p>
    </div>
  </div>
);

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loadingP, setLoadingP] = useState(true);
  const [loadingC, setLoadingC] = useState(true);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // {type, id, label}
  const [pwOpen, setPwOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const onExportCSV = async () => {
    setExporting(true);
    try {
      await adminExportContactCSV();
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setExporting(false);
    }
  };

  const loadProducts = async () => {
    setLoadingP(true);
    try {
      setProducts(await adminListProducts());
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoadingP(false);
    }
  };

  const loadContacts = async () => {
    setLoadingC(true);
    try {
      setContacts(await adminListContact());
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoadingC(false);
    }
  };

  useEffect(() => {
    loadProducts();
    loadContacts();
  }, []);

  const onSaveProduct = async (data) => {
    setSubmitting(true);
    try {
      if (editing) {
        await adminUpdateProduct(editing.id, data);
        toast.success("Product updated");
      } else {
        await adminCreateProduct(data);
        toast.success("Product created");
      }
      setFormOpen(false);
      setEditing(null);
      await loadProducts();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      if (confirmDelete.type === "product") {
        await adminDeleteProduct(confirmDelete.id);
        toast.success("Product deleted");
        await loadProducts();
      } else {
        await adminDeleteContact(confirmDelete.id);
        toast.success("Submission deleted");
        await loadContacts();
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setConfirmDelete(null);
    }
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-background">
      {/* Top bar */}
      <header className="bg-white border-b border-border sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-2.5">
            <span className="w-9 h-9 rounded-xl bg-primary text-primary-foreground grid place-items-center">
              <Stethoscope className="w-4 h-4" />
            </span>
            <div>
              <p className="font-heading font-bold text-sm leading-none">
                Ayushman Admin
              </p>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                Pediarange Console
              </p>
            </div>
          </Link>

          <div className="flex items-center gap-3">
            <Link to="/" className="hidden sm:block text-sm text-foreground/75 hover:text-primary">
              View site →
            </Link>
            <div className="hidden md:flex items-center gap-2 bg-secondary px-3 py-1.5 rounded-full">
              <span className="w-7 h-7 rounded-full bg-primary text-primary-foreground grid place-items-center font-bold text-xs">
                {user?.name?.[0] || "A"}
              </span>
              <span className="text-xs font-medium text-foreground/80">
                {user?.email}
              </span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPwOpen(true)}
              data-testid="admin-change-password-btn"
              className="rounded-full"
              title="Change password"
            >
              <KeyRound className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Password</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                logout();
                navigate("/admin/login");
              }}
              data-testid="admin-logout-btn"
              className="rounded-full"
            >
              <LogOut className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        <div className="mb-8">
          <h1 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-foreground">
            Welcome back, {user?.name || "Admin"} 👋
          </h1>
          <p className="mt-1.5 text-muted-foreground">
            Manage your Pediarange product catalogue and contact form submissions.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          <StatCard icon={Package} label="Products" value={products.length} accent="bg-primary" />
          <StatCard
            icon={MailOpen}
            label="Submissions"
            value={contacts.length}
            accent="bg-foreground"
          />
          <StatCard
            icon={RefreshCw}
            label="Today"
            value={
              contacts.filter((c) => {
                const d = new Date(c.created_at);
                const today = new Date();
                return d.toDateString() === today.toDateString();
              }).length
            }
            accent="bg-accent text-accent-foreground"
          />
        </div>

        <Tabs defaultValue="products" className="w-full">
          <TabsList className="rounded-full bg-secondary p-1 h-11">
            <TabsTrigger
              value="products"
              data-testid="admin-tab-products"
              className="rounded-full px-5 data-[state=active]:bg-white data-[state=active]:text-primary"
            >
              <Package className="w-4 h-4 mr-2" /> Products
            </TabsTrigger>
            <TabsTrigger
              value="submissions"
              data-testid="admin-tab-submissions"
              className="rounded-full px-5 data-[state=active]:bg-white data-[state=active]:text-primary"
            >
              <MailOpen className="w-4 h-4 mr-2" /> Submissions
            </TabsTrigger>
          </TabsList>

          {/* PRODUCTS */}
          <TabsContent value="products" className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-bold text-xl text-foreground">
                Pediarange Products
              </h2>
              <Button
                onClick={() => {
                  setEditing(null);
                  setFormOpen(true);
                }}
                className="rounded-full bg-primary hover:bg-primary/90"
                data-testid="admin-add-product-btn"
              >
                <Plus className="w-4 h-4 mr-2" /> Add product
              </Button>
            </div>

            {loadingP ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="aspect-[4/3] bg-secondary/40 rounded-2xl animate-pulse"
                  />
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-border">
                <Package className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
                <p className="font-heading font-semibold text-foreground">
                  No products yet
                </p>
                <p className="text-sm text-muted-foreground">
                  Click "Add product" to create your first one.
                </p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {products.map((p) => (
                  <article
                    key={p.id}
                    data-testid={`admin-product-${p.slug}`}
                    className="bg-white border border-border rounded-2xl overflow-hidden"
                  >
                    <div
                      className="aspect-[5/3] flex items-center justify-center p-4"
                      style={{
                        background: `linear-gradient(135deg, ${p.color_theme}15, ${p.color_theme}05)`,
                      }}
                    >
                      <img
                        src={p.image}
                        alt={p.name}
                        className="max-h-full max-w-full object-contain rounded-lg"
                      />
                    </div>
                    <div className="p-4">
                      <Badge
                        className="rounded-full text-white border-0 mb-2"
                        style={{ backgroundColor: p.color_theme }}
                      >
                        {p.category}
                      </Badge>
                      <h3 className="font-heading font-bold text-lg text-foreground">
                        {p.name}
                      </h3>
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                        {p.tagline}
                      </p>
                      <div className="flex items-center gap-2 mt-4">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEditing(p);
                            setFormOpen(true);
                          }}
                          className="rounded-full flex-1"
                          data-testid={`admin-edit-${p.slug}`}
                        >
                          <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            setConfirmDelete({
                              type: "product",
                              id: p.id,
                              label: p.name,
                            })
                          }
                          className="rounded-full text-destructive hover:bg-destructive/10 hover:text-destructive border-destructive/30"
                          data-testid={`admin-delete-${p.slug}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </TabsContent>

          {/* SUBMISSIONS */}
          <TabsContent value="submissions" className="mt-6">
            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
              <h2 className="font-heading font-bold text-xl text-foreground">
                Contact Submissions
              </h2>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onExportCSV}
                  disabled={exporting || contacts.length === 0}
                  className="rounded-full"
                  data-testid="admin-export-csv-btn"
                >
                  <Download className="w-3.5 h-3.5 mr-2" />
                  {exporting ? "Exporting…" : "Export CSV"}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={loadContacts}
                  className="rounded-full"
                  data-testid="admin-refresh-contacts"
                >
                  <RefreshCw className="w-3.5 h-3.5 mr-2" /> Refresh
                </Button>
              </div>
            </div>

            {loadingC ? (
              <div className="space-y-3">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="h-24 bg-secondary/40 rounded-2xl animate-pulse"
                  />
                ))}
              </div>
            ) : contacts.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-border">
                <MailOpen className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
                <p className="font-heading font-semibold text-foreground">
                  Inbox is clear
                </p>
                <p className="text-sm text-muted-foreground">
                  Submissions from the contact form will appear here.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {contacts.map((c) => (
                  <article
                    key={c.id}
                    data-testid={`admin-submission-${c.id}`}
                    className="bg-white border border-border rounded-2xl p-5"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="font-heading font-bold text-foreground">
                          {c.name}
                        </h3>
                        <div className="text-xs text-muted-foreground flex flex-wrap items-center gap-2 mt-1">
                          <a
                            href={`mailto:${c.email}`}
                            className="text-primary hover:underline flex items-center gap-1"
                          >
                            {c.email} <ExternalLink className="w-3 h-3" />
                          </a>
                          {c.phone && <span>· {c.phone}</span>}
                          {c.organization && <span>· {c.organization}</span>}
                          {c.role && <span>· {c.role}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">
                          {formatDate(c.created_at)}
                        </span>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            setConfirmDelete({
                              type: "contact",
                              id: c.id,
                              label: `${c.name} (${c.email})`,
                            })
                          }
                          className="h-8 w-8 p-0 text-destructive hover:bg-destructive/10"
                          data-testid={`admin-delete-submission-${c.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <p className="mt-3 text-sm text-foreground/85 leading-relaxed bg-secondary/40 rounded-xl p-3 whitespace-pre-wrap">
                      {c.message}
                    </p>
                  </article>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      <ProductFormDialog
        open={formOpen}
        onOpenChange={(o) => {
          setFormOpen(o);
          if (!o) setEditing(null);
        }}
        initial={editing}
        onSubmit={onSaveProduct}
        submitting={submitting}
      />

      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete confirmation</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete{" "}
              <span className="font-semibold text-foreground">
                {confirmDelete?.label}
              </span>
              ? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-delete-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="confirm-delete-yes"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminDashboard;
